import { useCallback, useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { authReducerActionType } from "../../types/globalTypes";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import {
  convertProjectLead as convertProjectLeadApi,
  createProjectLead as createProjectLeadApi,
  discardProjectLead as discardProjectLeadApi,
  getProjectLeadById,
  getProjectLeads,
  updateProjectLead as updateProjectLeadApi,
} from "../../api/projectLeads";
import {
  ConvertProjectLeadDto,
  CreateProjectLeadDto,
  DiscardProjectLeadDto,
  ProjectLeadDto,
  UpdateProjectLeadDto,
} from "../../types/projectLeadTypes";

/** Hook wrapping the Lead API with react-query. */
const useProjectLeads = () => {
  const { dispatchAuth } = useContext(AuthContext);
  const queryClient = useQueryClient();

  const { data: projectLeadsData, isLoading: isProjectLeadsLoading } = useQuery({
    queryKey: ["projectLeads"],
    queryFn: getProjectLeads,
  });
  const projectLeads: ProjectLeadDto[] = projectLeadsData ? projectLeadsData.data : [];

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["projectLeads"] });
    queryClient.invalidateQueries({ queryKey: ["projects"] });
    queryClient.invalidateQueries({ queryKey: ["tenderedProjects"] });
  };

  const onAuthError = (err: AxiosError) => {
    if (err.response?.status === 401) {
      dispatchAuth({ type: authReducerActionType.deauthenticate });
    }
  };

  // Keep this stable so callers can depend on it in effects without suppressing hook lint rules.
  const fetchProjectLead = useCallback(
    async (leadId: number): Promise<ProjectLeadDto> => {
      const res = await queryClient.fetchQuery({
        queryKey: ["projectLead", leadId],
        queryFn: () => getProjectLeadById(leadId),
      });
      return res.data;
    },
    [queryClient]
  );

  const { mutateAsync: createMutation } = useMutation({
    mutationFn: (body: CreateProjectLeadDto) => createProjectLeadApi(body),
    onError: (err: AxiosError) => {
      showErrorMessage("Lead anlegen fehlgeschlagen");
      onAuthError(err);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projectLeads"] });
      showSuccessMessage("Lead erfolgreich angelegt");
    },
  });
  const createProjectLead = async (body: CreateProjectLeadDto) => (await createMutation(body)).data;

  const { mutateAsync: updateMutation } = useMutation({
    mutationFn: ({ leadId, body }: { leadId: number; body: UpdateProjectLeadDto }) =>
      updateProjectLeadApi(leadId, body),
    onError: (err: AxiosError) => {
      showErrorMessage("Lead aktualisieren fehlgeschlagen");
      onAuthError(err);
    },
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["projectLeads"] });
      queryClient.invalidateQueries({ queryKey: ["projectLead", vars.leadId] });
      showSuccessMessage("Lead erfolgreich aktualisiert");
    },
  });
  const updateProjectLead = async (leadId: number, body: UpdateProjectLeadDto) =>
    (await updateMutation({ leadId, body })).data;

  const { mutateAsync: discardMutation } = useMutation({
    mutationFn: ({ leadId, body }: { leadId: number; body: DiscardProjectLeadDto }) =>
      discardProjectLeadApi(leadId, body),
    onError: (err: AxiosError) => {
      showErrorMessage("Lead verwerfen fehlgeschlagen");
      onAuthError(err);
    },
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["projectLeads"] });
      queryClient.invalidateQueries({ queryKey: ["projectLead", vars.leadId] });
      showSuccessMessage("Lead verworfen");
    },
  });
  const discardProjectLead = async (leadId: number, body: DiscardProjectLeadDto) =>
    (await discardMutation({ leadId, body })).data;

  const { mutateAsync: convertMutation } = useMutation({
    mutationFn: ({ leadId, body }: { leadId: number; body: ConvertProjectLeadDto }) =>
      convertProjectLeadApi(leadId, body),
    onError: (err: AxiosError) => {
      showErrorMessage("Projektdaten aus Lead speichern fehlgeschlagen");
      onAuthError(err);
    },
    onSuccess: (_res, vars) => {
      invalidateAll();
      queryClient.invalidateQueries({ queryKey: ["projectLead", vars.leadId] });
      showSuccessMessage("Projektdaten erfolgreich erfasst. Projekt kann jetzt ausgeschrieben werden.");
    },
  });
  const convertProjectLead = async (leadId: number, body: ConvertProjectLeadDto) =>
    (await convertMutation({ leadId, body })).data;

  return {
    projectLeads,
    isProjectLeadsLoading,
    fetchProjectLead,
    createProjectLead,
    updateProjectLead,
    discardProjectLead,
    convertProjectLead,
  };
};

export default useProjectLeads;
